import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRoundIcon, Loader2Icon, MailIcon, MonitorSmartphoneIcon, PlusIcon, ShieldCheckIcon, ShieldOffIcon, SmartphoneIcon, Trash2Icon } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SectionTitle } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RecoveryCodes } from '@/features/auth/RecoveryCodes';
import { methodTitle } from '@/features/auth/MfaStep';
import { api, errorMessage } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import type { MfaMethodDto, MfaMethodType, MfaStatus, TotpEnrollment, TrustedDeviceDto } from '@/lib/types';

const KEY = ['auth', 'me', 'mfa'] as const;
const DEVICES_KEY = ['auth', 'me', 'mfa', 'devices'] as const;

const TYPE_ICON: Record<MfaMethodType, typeof KeyRoundIcon> = { totp: SmartphoneIcon, sms: SmartphoneIcon, email: MailIcon };

function fmtAgo(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return new Date(iso).toLocaleDateString();
}

/** Account → Two-step verification: enroll, remove and recover second factors; see trusted devices. */
export function TwoStepSection() {
  const me = useMe();
  const { refreshMe } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api.auth.twoStep.status(), initialData: me.mfa, staleTime: 15_000 });
  const status: MfaStatus = q.data;
  const policy = status.policy;
  const [adding, setAdding] = useState<MfaMethodType | null>(null);
  const [removing, setRemoving] = useState<MfaMethodDto | null>(null);
  const [codesDialog, setCodesDialog] = useState<string[] | null>(null);
  const [confirmCodes, setConfirmCodes] = useState(false);
  const [showDevices, setShowDevices] = useState(false);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: KEY });
    await refreshMe();
  };

  const remove = useMutation({
    mutationFn: (id: string) => api.auth.twoStep.remove(id),
    onSuccess: async () => {
      toast.success('Method removed');
      setRemoving(null);
      await refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const regenerate = useMutation({
    mutationFn: () => api.auth.twoStep.regenerateRecoveryCodes(),
    onSuccess: async (res) => {
      setConfirmCodes(false);
      setCodesDialog(res.recoveryCodes);
      await refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const allowed = (Object.entries(policy.methods) as Array<[MfaMethodType, boolean]>).filter(([, on]) => on).map(([t]) => t);
  const off = policy.mode === 'off';

  return (
    <section className="bg-card border p-6">
      <SectionTitle className="mb-2" actions={status.enrolled ? <Badge variant="secondary">On</Badge> : <Badge variant="outline">Off</Badge>}>
        Two-step verification
      </SectionTitle>
      <p className="text-muted-foreground mb-5 text-sm">
        {off
          ? 'Your organization has not turned on two-step verification. Anything you set up here is kept and used once they do.'
          : policy.mode === 'required'
            ? 'Your organization requires a second step when you sign in. Keep at least one method and save your recovery codes.'
            : 'Add a second step to signing in: a code from an authenticator app, a text, or an email.'}
      </p>

      <div className="grid gap-5">
        <div className="grid gap-2">
          {status.methods.length === 0 ? (
            <div className="text-muted-foreground flex items-center gap-3 border border-dashed p-3 text-sm">
              <ShieldOffIcon className="size-4 shrink-0" aria-hidden /> No verification methods yet.
            </div>
          ) : (
            status.methods.map((m) => {
              const Icon = TYPE_ICON[m.type];
              return (
                <div key={m.id} className="flex items-center gap-3 border p-3">
                  <Icon className="text-muted-foreground size-4 shrink-0" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{methodTitle(m)}</div>
                    <div className="text-muted-foreground text-xs">
                      {m.destination ? `${m.destination} · ` : ''}last used {fmtAgo(m.lastUsedAt)}
                      {!policy.methods[m.type] ? ' · not allowed by policy any more' : ''}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon-sm" aria-label={`Remove ${methodTitle(m)}`} onClick={() => setRemoving(m)}>
                    <Trash2Icon aria-hidden />
                  </Button>
                </div>
              );
            })
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {allowed.map((t) => (
            <Button key={t} variant="outline" size="sm" onClick={() => setAdding(t)}>
              <PlusIcon aria-hidden /> {t === 'totp' ? 'Authenticator app' : t === 'sms' ? 'Text message' : 'Email'}
            </Button>
          ))}
        </div>

        {status.enrolled ? (
          <div className="grid gap-3 border-t pt-4 text-sm sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <KeyRoundIcon className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
              <div className="min-w-0">
                <div className="font-medium">Recovery codes</div>
                <p className="text-muted-foreground text-xs">
                  {status.recoveryCodes.remaining} of {status.recoveryCodes.total} left. Each signs you in once if you lose your other methods.
                </p>
                <Button variant="link" size="sm" className="h-auto px-0 py-1" onClick={() => setConfirmCodes(true)}>
                  Generate new codes
                </Button>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MonitorSmartphoneIcon className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
              <div className="min-w-0">
                <div className="font-medium">Remembered devices</div>
                <p className="text-muted-foreground text-xs">
                  {status.trustedDevices === 0 ? 'No browser skips the second step.' : `${status.trustedDevices} ${status.trustedDevices === 1 ? 'browser skips' : 'browsers skip'} the second step for up to ${policy.rememberDeviceDays} days.`}
                </p>
                {status.trustedDevices > 0 ? (
                  <Button variant="link" size="sm" className="h-auto px-0 py-1" onClick={() => setShowDevices(true)}>
                    Manage devices
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {adding ? <AddMethodDialog type={adding} onClose={() => setAdding(null)} onEnrolled={async (codes) => { await refresh(); if (codes) setCodesDialog(codes); }} /> : null}

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={removing ? `Remove ${methodTitle(removing)}?` : ''}
        description={status.methods.length === 1 ? 'This is your only method. Removing it turns two-step verification off for your account and discards your recovery codes.' : 'You will no longer be able to verify with this method.'}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (removing) remove.mutate(removing.id);
        }}
      />

      <ConfirmDialog
        open={confirmCodes}
        onOpenChange={setConfirmCodes}
        title="Generate new recovery codes?"
        description="Your current codes stop working immediately. You will see the new ones once."
        confirmLabel="Generate"
        loading={regenerate.isPending}
        onConfirm={() => regenerate.mutate()}
      />

      <Dialog open={!!codesDialog} onOpenChange={(o) => !o && setCodesDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Your recovery codes</DialogTitle>
            <DialogDescription>Store them somewhere safe. They are shown only now.</DialogDescription>
          </DialogHeader>
          {codesDialog ? <RecoveryCodes codes={codesDialog} appName={me.branding.appName} onDone={() => setCodesDialog(null)} /> : null}
        </DialogContent>
      </Dialog>

      {showDevices ? <DevicesDialog onClose={() => setShowDevices(false)} onChanged={refresh} /> : null}
    </section>
  );
}

// ---------------------------------------------------------------- add a method

function AddMethodDialog({ type, onClose, onEnrolled }: { type: MfaMethodType; onClose: () => void; onEnrolled: (recoveryCodes: string[] | null) => Promise<void> }) {
  const me = useMe();
  const [totp, setTotp] = useState<TotpEnrollment | null>(null);
  const [methodId, setMethodId] = useState<string | null>(null);
  const [destination, setDestination] = useState(type === 'sms' ? (me.user.phoneDisplay ?? me.user.phone ?? '') : type === 'email' ? me.user.email : '');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (type !== 'totp') return;
    let cancelled = false;
    setBusy(true);
    api.auth.twoStep
      .totpStart()
      .then((t) => {
        if (!cancelled) setTotp(t);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [type]);

  const startOtp = async (e: FormEvent) => {
    e.preventDefault();
    if (type === 'totp') return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.auth.twoStep.otpStart(type, destination.trim());
      setMethodId(res.method.id);
      setSentTo(res.method.destination);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = type === 'totp' && totp ? await api.auth.twoStep.totpConfirm(totp.method.id, code.trim()) : methodId ? await api.auth.twoStep.otpConfirm(methodId, code.trim()) : null;
      if (!res) return;
      toast.success('Two-step verification method added');
      onClose();
      await onEnrolled(res.recoveryCodes);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const title = type === 'totp' ? 'Set up an authenticator app' : type === 'sms' ? 'Add a text-message method' : 'Add an email method';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{type === 'totp' ? 'Microsoft Authenticator, Google Authenticator, 1Password and similar apps all work.' : 'We will send a 6-digit code to confirm it works.'}</DialogDescription>
        </DialogHeader>
        <FormError message={error} />
        {type === 'totp' ? (
          <form onSubmit={confirm} className="grid gap-4" noValidate>
            {totp ? (
              <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-start">
                <img src={totp.qrDataUrl} alt="QR code for your authenticator app" width={176} height={176} className="border bg-white p-1" />
                <div className="grid gap-2 text-sm">
                  <p className="font-medium">1. Scan this code in the app</p>
                  <p className="text-muted-foreground text-xs">
                    Or enter the key manually: <span className="text-foreground font-mono tracking-wider select-all">{totp.secretFormatted}</span>
                  </p>
                  <p className="mt-1 font-medium">2. Enter the code the app shows</p>
                  <Input inputMode="numeric" autoComplete="one-time-code" maxLength={7} placeholder="123 456" className="text-center text-lg tracking-[0.3em]" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
                </div>
              </div>
            ) : (
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <Loader2Icon className="size-4 animate-spin" aria-hidden /> Preparing…
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !totp || code.trim().length < 6}>
                {busy ? 'Checking…' : 'Turn on'}
              </Button>
            </div>
          </form>
        ) : !methodId ? (
          <form onSubmit={startOtp} className="grid gap-4" noValidate>
            <Field label={type === 'sms' ? 'Mobile number' : 'Email address'} htmlFor="add-destination">
              <Input id="add-destination" type={type === 'sms' ? 'tel' : 'email'} inputMode={type === 'sms' ? 'tel' : 'email'} autoFocus value={destination} onChange={(e) => setDestination(e.target.value)} placeholder={type === 'sms' ? '(407) 555-0123' : 'you@company.com'} />
            </Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || destination.trim().length < 3}>
                {busy ? 'Sending…' : 'Send code'}
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={confirm} className="grid gap-4" noValidate>
            <p className="text-muted-foreground text-sm">
              We sent a code to <span className="text-foreground font-medium">{sentTo}</span>.
            </p>
            <Field label="Verification code" htmlFor="add-code">
              <Input id="add-code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} placeholder="123 456" className="text-center text-lg tracking-[0.3em]" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
            </Field>
            <div className="flex items-center justify-between gap-2">
              <Button type="button" variant="link" size="sm" className="px-0" onClick={() => methodId && void api.auth.twoStep.otpResend(methodId).then(() => toast.success('Code sent again')).catch((err) => setError(errorMessage(err)))}>
                Resend code
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={busy || code.trim().length < 6}>
                  {busy ? 'Checking…' : 'Turn on'}
                </Button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- trusted devices

function DevicesDialog({ onClose, onChanged }: { onClose: () => void; onChanged: () => Promise<void> }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: DEVICES_KEY, queryFn: () => api.auth.twoStep.devices() });
  const forget = useMutation({
    mutationFn: (id: string | null) => (id ? api.auth.twoStep.forgetDevice(id) : api.auth.twoStep.forgetDevices()),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: DEVICES_KEY });
      await onChanged();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const items: TrustedDeviceDto[] = q.data?.items ?? [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remembered devices</DialogTitle>
          <DialogDescription>Browsers that skip the second step. Forget one you do not recognise.</DialogDescription>
        </DialogHeader>
        <ul className="grid gap-2">
          {items.map((d) => (
            <li key={d.id} className="flex items-center gap-3 border p-3 text-sm">
              <MonitorSmartphoneIcon className="text-muted-foreground size-4 shrink-0" aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">
                  {shortUa(d.userAgent)} {d.current ? <Badge variant="secondary">This browser</Badge> : null}
                </div>
                <div className="text-muted-foreground text-xs">
                  last used {fmtAgo(d.lastUsedAt ?? d.createdAt)} · until {new Date(d.expiresAt).toLocaleDateString()}
                  {d.ip ? ` · ${d.ip}` : ''}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => forget.mutate(d.id)} disabled={forget.isPending}>
                Forget
              </Button>
            </li>
          ))}
          {!q.isPending && items.length === 0 ? <li className="text-muted-foreground text-sm">No remembered devices.</li> : null}
        </ul>
        <div className="flex justify-between gap-2">
          <Button variant="outline" onClick={() => forget.mutate(null)} disabled={forget.isPending || items.length === 0}>
            <ShieldCheckIcon aria-hidden /> Forget all
          </Button>
          <Button onClick={onClose}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function shortUa(ua: string | null): string {
  if (!ua) return 'Unknown browser';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}
